import React, { useState } from "react";
import { useRecordContext } from "react-admin";
import CustomReferenceField from "../Common/CustomReferenceField";
import { UserType } from "../../enums/common";
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { formatDate, formatDateTime, formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import CouponAppPreview from "./CouponAppPreview";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v: any) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN")}` : null;

// ── Primitives ────────────────────────────────────────────────────────────────

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      overflow: "hidden",
      height: "100%",
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.25,
        bgcolor: "action.selected",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" fontWeight={700} letterSpacing={0.4} textTransform="uppercase" fontSize="0.7rem">
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 0 }}>
      {children}
    </Box>
  </Box>
);

const Row = ({ label, children }: { label: string; children?: React.ReactNode }) => {
  const isEmpty = children == null || children === "" || children === false;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        py: 0.9,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
        gap: 1,
        opacity: isEmpty ? 0.45 : 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          minWidth: 155,
          color: "text.secondary",
          fontWeight: 600,
          flexShrink: 0,
          lineHeight: "20px",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, typography: "body2", lineHeight: "20px", wordBreak: "break-word" }}>
        {isEmpty ? <span style={{ fontStyle: "italic" }}>—</span> : children}
      </Box>
    </Box>
  );
};

const Bool = ({ value, trueText = "Yes", falseText = "No" }: { value: any; trueText?: string; falseText?: string }) => {
  const on = value === true || value === "true";
  return (
    <Chip
      label={on ? trueText : falseText}
      size="small"
      color={on ? "success" : "default"}
      variant={on ? "filled" : "outlined"}
      sx={{ fontSize: "0.7rem", height: 20 }}
    />
  );
};

const Tag = ({ label, color = "default" }: { label: string; color?: any }) => (
  <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontSize: "0.72rem", height: 20 }} />
);

const IdSet = ({ ids }: { ids: number[] }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(ids.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={0.75}>
        <Chip
          label={`${ids.length} IDs`}
          size="small"
          color="primary"
          sx={{ fontSize: "0.7rem", height: 20 }}
        />
        <Tooltip title={copied ? "Copied!" : "Copy all IDs"}>
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
            {copied ? <CheckIcon fontSize="inherit" color="success" /> : <ContentCopyIcon fontSize="inherit" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          maxHeight: 130,
          overflowY: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.75,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 2, bgcolor: "divider" },
        }}
      >
        {ids.map((id) => (
          <Chip key={id} label={id} size="small" variant="outlined" sx={{ fontSize: "0.7rem", height: 20 }} />
        ))}
      </Box>
    </Box>
  );
};

const RuleBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      mt: 0.5,
      px: 1.25,
      py: 1,
      bgcolor: "action.hover",
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    {children}
  </Box>
);

const RuleTypeChip = ({ type }: { type: string }) => (
  <Chip
    label={type}
    size="small"
    color={type === "inclusion" ? "success" : "error"}
    sx={{ fontSize: "0.7rem", height: 20, fontWeight: 700, textTransform: "capitalize" }}
  />
);

// ── Main component ────────────────────────────────────────────────────────────

const CouponMetaShowV2 = () => {
  const record = useRecordContext();
  const [heroCopied, setHeroCopied] = useState(false);

  const handleHeroCopy = () => {
    if (!record?.couponCode) return;
    navigator.clipboard.writeText(record.couponCode);
    setHeroCopied(true);
    setTimeout(() => setHeroCopied(false), 2000);
  };

  const usagePercent =
    record?.maximumUsageLimit && record?.totalTimesUsed != null
      ? Math.min((record.totalTimesUsed / record.maximumUsageLimit) * 100, 100)
      : null;

  const isExpired = record?.expiresAt && new Date(record.expiresAt) < new Date();
  const isUpcoming = record?.startsAt && new Date(record.startsAt) > new Date();

  const hasAnyRule =
    record?.dateInclusionExclusionRule ||
    record?.timeInclusionExclusionRule ||
    record?.userInclusionExclusionRule;

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>

        {/* ── Hero Banner ── */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
              bgcolor: "action.hover",
            }}
          >
            {/* Coupon code */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.5} textTransform="uppercase">
                Coupon Code
              </Typography>
              <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
                <Typography variant="h5" fontFamily="monospace" fontWeight={800} letterSpacing={3}>
                  {record?.couponCode ?? "—"}
                </Typography>
                <Tooltip title={heroCopied ? "Copied!" : "Copy code"}>
                  <IconButton size="small" onClick={handleHeroCopy} sx={{ p: 0.5 }}>
                    {heroCopied
                      ? <CheckIcon fontSize="small" color="success" />
                      : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

            {/* Status chips */}
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {record?.isActive !== undefined && (
                <Chip
                  label={record.isActive ? "Active" : "Inactive"}
                  color={record.isActive ? "success" : "error"}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                />
              )}
              {isExpired && (
                <Chip label="Expired" color="error" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
              )}
              {isUpcoming && (
                <Chip label="Upcoming" color="warning" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
              )}
              {record?.visibleOnApp === false && (
                <Chip label="Hidden on App" color="default" variant="outlined" size="small" />
              )}
              {record?.couponType && (
                <Chip label={record.couponType} variant="outlined" size="small" sx={{ fontSize: "0.72rem" }} />
              )}
              {record?.productType && (
                <Chip label={record.productType} color="primary" variant="outlined" size="small" sx={{ fontSize: "0.72rem" }} />
              )}
              {record?.couponValue != null && (
                <Chip
                  label={`Value: ${record.couponValue}`}
                  color="secondary"
                  size="small"
                  sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                />
              )}
            </Box>

            {/* Header / description */}
            {record?.couponHeader && (
              <Box ml="auto" sx={{ textAlign: "right" }}>
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  "{record.couponHeader}"
                </Typography>
                {record?.couponDescription && (
                  <Typography variant="caption" color="text.disabled">
                    {record.couponDescription}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        {/* ── App Preview ── */}
        <Grid item xs={12} md={4}>
          <Section title="Customer App Preview">
            <CouponAppPreview
              couponCode={record?.couponCode}
              couponType={record?.couponType}
              couponValue={record?.couponValue}
              couponDescription={record?.couponDescription}
              minimumOrderValue={record?.minimumOrderValue}
              termsAndConditions={record?.termsAndConditions || []}
              errorMessages={
                Array.isArray(record?.errorMessages)
                  ? record?.errorMessages.join(", ")
                  : record?.errorMessages
              }
              discountCouponSkuId={record?.discountCouponSkuId}
              showLabel={false}
            />
          </Section>
        </Grid>

        {/* ── Basic Info ── */}
        <Grid item xs={12} md={4}>
          <Section title="Basic Info">
            <Row label="ID">{record?.id}</Row>
            <Row label="Coupon Code">
              <Typography variant="body2" fontWeight={700} fontFamily="monospace">
                {record?.couponCode}
              </Typography>
            </Row>
            <Row label="Coupon Type">
              {record?.couponType ? <Tag label={record.couponType} /> : null}
            </Row>
            <Row label="Coupon Value">{record?.couponValue}</Row>
            <Row label="Product Type">
              {record?.productType ? <Tag label={record.productType} color="primary" /> : null}
            </Row>
            <Row label="Coupon Header">{record?.couponHeader}</Row>
            <Row label="Description">{record?.couponDescription}</Row>
            <Row label="Display String">{record?.couponDisplayString}</Row>
          </Section>
        </Grid>

        {/* ── Status & Visibility ── */}
        <Grid item xs={12} md={4}>
          <Section title="Status & Visibility">
            <Row label="Is Active">
              <Bool value={record?.isActive} trueText="Active" falseText="Inactive" />
            </Row>
            <Row label="Visible On App">
              <Bool value={record?.visibleOnApp} trueText="Visible" falseText="Hidden" />
            </Row>
            <Row label="Reason">{record?.reason}</Row>
            <Row label="Created By Email">{record?.createdByEmail}</Row>
            <Row label="Created By">
              {record?.createdByType === UserType.CUSTOMER
                ? <CustomReferenceField label="" listName="Customers" source="createdBy" />
                : <CustomReferenceField label="" listName="AdminUsers" source="createdBy" />
              }
            </Row>
          </Section>
        </Grid>

        {/* ── Dates ── */}
        <Grid item xs={12} md={4}>
          <Section title="Dates">
            <Row label="Starts At">
              {record?.startsAt ? (
                <Box display="flex" alignItems="center" gap={0.75}>
                  {formatDateTime(record.startsAt)}
                  {isUpcoming && <Chip label="Upcoming" color="warning" size="small" sx={{ fontSize: "0.65rem", height: 18 }} />}
                </Box>
              ) : null}
            </Row>
            <Row label="Expires At">
              {record?.expiresAt ? (
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Typography
                    variant="body2"
                    color={isExpired ? "error.main" : "inherit"}
                    fontWeight={isExpired ? 600 : 400}
                  >
                    {formatDateTime(record.expiresAt)}
                  </Typography>
                  {isExpired && <Chip label="Expired" color="error" size="small" sx={{ fontSize: "0.65rem", height: 18 }} />}
                </Box>
              ) : null}
            </Row>
            <Row label="Created At">{record?.createdAt ? formatDateTimeWithSeconds(record.createdAt) : null}</Row>
            <Row label="Updated At">{record?.updatedAt ? formatDateTimeWithSeconds(record.updatedAt) : null}</Row>
          </Section>
        </Grid>

        {/* ── Usage & Limits ── */}
        <Grid item xs={12} md={4}>
          <Section title="Usage & Limits">
            <Row label="Min Order Value">{fmt(record?.minimumOrderValue)}</Row>
            <Row label="Max Discount Amount">{fmt(record?.maximumDiscountAmount)}</Row>
            <Row label="Min Total After Discount">{fmt(record?.minimumTotalAfterDiscount)}</Row>
            <Row label="Usage Limit / Customer">{record?.usageLimitPerCustomer}</Row>
            <Row label="Max Usage Limit">{record?.maximumUsageLimit}</Row>
            <Row label="Total Times Used">{record?.totalTimesUsed}</Row>
            {usagePercent !== null && (
              <Box sx={{ py: 0.9, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Usage Progress</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {record.totalTimesUsed} / {record.maximumUsageLimit} ({Math.round(usagePercent)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usagePercent}
                  color={usagePercent >= 90 ? "error" : usagePercent >= 70 ? "warning" : "success"}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}
            <Row label="Unique Customers Used">{record?.uniqueCustomerUsed}</Row>
          </Section>
        </Grid>

        {/* ── Cashback ── */}
        <Grid item xs={12} md={4}>
          <Section title="Cashback">
            <Row label="Cashback Enabled">
              <Bool value={record?.isCashbackEnabled} trueText="Enabled" falseText="Disabled" />
            </Row>
            {record?.isCashbackEnabled && (
              <>
                <Row label="Cashback Type">
                  {record?.cashbackType ? <Tag label={record.cashbackType} /> : null}
                </Row>
                <Row label="Cashback Value">{record?.cashbackValue}</Row>
                <Row label="Cashback Expiry Days">
                  {record?.cashbackExpiryDays != null ? `${record.cashbackExpiryDays} days` : null}
                </Row>
              </>
            )}
          </Section>
        </Grid>

        {/* ── User & Area Sets ── */}
        <Grid item xs={12} md={4}>
          <Section title="User & Area Sets">
            <Row label="User Set">
              {record?.userSet?.length > 0 ? <IdSet ids={record.userSet} /> : null}
            </Row>
            <Row label="Area Set">
              {record?.areaSet?.length > 0 ? <IdSet ids={record.areaSet} /> : null}
            </Row>
          </Section>
        </Grid>

        {/* ── Inclusion / Exclusion Rules ── */}
        <Grid item xs={12} md={6}>
          <Section title="Inclusion / Exclusion Rules">
            {!hasAnyRule ? (
              <Typography variant="body2" color="text.disabled" sx={{ py: 1.5, textAlign: "center", fontStyle: "italic" }}>
                No rules configured
              </Typography>
            ) : (
              <>
                {/* Date rule */}
                {record?.dateInclusionExclusionRule && (
                  <Row label="Date Rule">
                    <RuleBox>
                      <Box display="flex" gap={0.75} flexWrap="wrap" mb={0.75}>
                        <RuleTypeChip type={record.dateInclusionExclusionRule.type} />
                        <Tag label={record.dateInclusionExclusionRule.period} />
                      </Box>
                      {record.dateInclusionExclusionRule.period === "DAYS_OF_WEEK"
                        ? <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {record.dateInclusionExclusionRule.values?.map((v: string) => <Tag key={v} label={v} />)}
                          </Box>
                        : <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ py: 0.5, fontSize: "0.72rem", fontWeight: 700 }}>Start</TableCell>
                                <TableCell sx={{ py: 0.5, fontSize: "0.72rem", fontWeight: 700 }}>End</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {record.dateInclusionExclusionRule.values?.map((each: any, i: number) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ py: 0.5, fontSize: "0.72rem" }}>{each.startDate ? formatDate(each.startDate) : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.5, fontSize: "0.72rem" }}>{each.endDate ? formatDate(each.endDate) : "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                      }
                    </RuleBox>
                  </Row>
                )}

                {/* Time rule */}
                {record?.timeInclusionExclusionRule && (
                  <Row label="Time Rule">
                    <RuleBox>
                      <Box mb={0.75}>
                        <RuleTypeChip type={record.timeInclusionExclusionRule.type} />
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {record.timeInclusionExclusionRule.timeSlots?.map((s: string) => (
                          <Tag key={s} label={s} color="primary" />
                        ))}
                      </Box>
                    </RuleBox>
                  </Row>
                )}

                {/* User rule */}
                {record?.userInclusionExclusionRule && (
                  <Row label="User Rule">
                    <RuleBox>
                      <Box display="flex" gap={0.75} flexWrap="wrap">
                        <RuleTypeChip type={record.userInclusionExclusionRule.type} />
                        <Tag label={record.userInclusionExclusionRule.rule} />
                      </Box>
                    </RuleBox>
                  </Row>
                )}
              </>
            )}
          </Section>
        </Grid>

        {/* ── Terms & Error Messages ── */}
        <Grid item xs={12} md={6}>
          <Section title="Terms & Error Messages">
            <Row label="Terms & Conditions">
              {record?.termsAndConditions?.length > 0
                ? <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {record.termsAndConditions.map((t: string, i: number) => (
                      <Box key={i} display="flex" gap={1} alignItems="flex-start">
                        <Chip
                          label={i + 1}
                          size="small"
                          color="default"
                          sx={{ fontSize: "0.65rem", height: 18, minWidth: 24, flexShrink: 0, mt: "1px" }}
                        />
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{t}</Typography>
                      </Box>
                    ))}
                  </Box>
                : null}
            </Row>
            <Divider />
            <Row label="Error Messages">
              {Array.isArray(record?.errorMessages)
                ? record.errorMessages.join(", ")
                : record?.errorMessages}
            </Row>
          </Section>
        </Grid>

      </Grid>
    </Box>
  );
};

export default CouponMetaShowV2;
